import {useEffect, useRef} from 'react';

import {stringArraysAreEqual} from '@/components/features/search/filters/FiltersTabletDesktop/utils/helpers';
import useFilters from '@/components/features/search/filters/FiltersUtils/useFilters';
import {type DietaryOptionEnum} from '@/graphql/types';
import {useEventBarV2} from '../EventBarV2Provider';
import useFilterSearchSignature from './useFilterSearchSignature';

// Keeps the Who panel's dietary chips in step with the committed dietary filters, so clearing or
// editing them elsewhere on the page (the filters sidebar, filter chips, "clear all") is reflected
// in the bar. `hasUserEditedDietaryRef` means "diverged from the committed filters", not "ever
// toggled": an uncommitted toggle in the panel is left untouched until a search commits it — or
// until a filter runs a search of its own, which the toggle was never part of.
const useSyncDietaryWithFilters = (): void => {
  const {dietaryOptions, setDietaryOptions, hasUserEditedDietaryRef} = useEventBarV2();
  const {filters} = useFilters();
  const {dietaryOptions: committedDietary} = filters;

  const inSync = stringArraysAreEqual(dietaryOptions, committedDietary);

  const filtersSignature = useFilterSearchSignature();
  const previousFiltersSignatureRef = useRef(filtersSignature);

  useEffect(() => {
    const filtersChanged = previousFiltersSignatureRef.current !== filtersSignature;
    previousFiltersSignatureRef.current = filtersSignature;

    if (hasUserEditedDietaryRef.current) {
      // Resume mirroring once the bar is back in step with the committed filters (a search commits
      // the toggled selection, or the user toggles back to match) — or once a filter triggers a new
      // search, which drops the uncommitted toggle.
      if (!inSync && !filtersChanged) return;
      hasUserEditedDietaryRef.current = false;
    }

    // Mirror the committed dietary filters, reflecting external adds, removes, and clears.
    if (!inSync) setDietaryOptions(committedDietary as DietaryOptionEnum[]);
  }, [committedDietary, filtersSignature, inSync, setDietaryOptions, hasUserEditedDietaryRef]);
};

export default useSyncDietaryWithFilters;
